(function () {
    class NewsGraphVisualizer {
        constructor(container) {
            this.container = container;
            this._destroyed = false;
        }

        mount(data) {
            this._data = data;
            this._nodes = window.NewsGraph.selectNodes(data);
            this._links = window.NewsGraph.selectLinks(data.relationships || [], this._nodes);
            this._tickerPriceMap = NewsProxyAPIManager.buildTickerPriceMap(data);
            this._preIpoSymbols = new Set(
                (data?.tickers || [])
                    .filter(t => t.possible_pre_ipo === true)
                    .map(t => String(t.symbol || '').toUpperCase().trim())
            );

            this._isLight = document.body.classList.contains('light') || document.body.classList.contains('cb-light');

            this._initAframeScene();
            
            this._envMap = window.NewsGraph.NewsGraphBuilder.setupEnvironmentMap(this._sceneEl);
            this._sceneEl.object3D.environment = this._envMap;
            
            if (this._sceneEl) {
            }

            const buildResult = window.NewsGraph.NewsGraphBuilder.buildNodes(this._nodes, this._pivotEl, false, this._links);
            this._meshMap = buildResult.meshMap;
            this._nodeEls = buildResult.nodeEls;
            this._coneInfos = buildResult.coneInfos;

            const THREE = window.THREE;
            this._pivot.quaternion.setFromAxisAngle(new THREE.Vector3(1, 0, 0), 0.15);

            this._separationRadius = window.NewsGraph.runForce(this._nodes, this._links, this._meshMap);

            this._originalPositions = new Map();
            this._nodes.forEach(node => {
                const el = this._meshMap[node.id];
                if (el) {
                    this._originalPositions.set(node.id, el.object3D.position.clone());
                }
            });

            this._orbitSphere = window.NewsGraph.NewsGraphBuilder.buildOrbitSphere(
                this._separationRadius, this._pivot, this._isLight
            );

            this._selectedNodeEl = null;
            this._hoveredNodeEl = null;

            this._onMouseMoveBound = (e) => this._handleMouseMove(e.clientX, e.clientY);
            this._onMouseLeaveBound = () => {
                if (this._hoveredNodeEl !== null) {
                    this._hoveredNodeEl = null;
                    this._updateNodeVisuals();
                }
            };

            this.container.addEventListener('mousemove', this._onMouseMoveBound);
            this.container.addEventListener('mouseleave', this._onMouseLeaveBound);

            this._labelMap = window.NewsGraph.NewsGraphBuilder.buildLabels(this._nodes, this._labelRoot);
            this._updateTheme();
            
            this._themeObserver = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        const isLight = document.body.classList.contains('light') || document.body.classList.contains('cb-light');
                        if (isLight !== this._isLight) {
                            this._isLight = isLight;
                            this._updateTheme();
                        }
                    }
                });
            });
            this._themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

            this._controls = new window.NewsGraph.NewsGraphControls(this.container, this._pivot, this._camera);
            this._controls.onClick = (x, y) => this._handleClick(x, y);

            this._updateLayoutAndCamera();

            this._resizeObserver = new ResizeObserver(() => {
                this._updateLayoutAndCamera();
            });
            this._resizeObserver.observe(this.container);

            this._animate();
        }

        _handleClick(clientX, clientY) {
            const rect = this.container.getBoundingClientRect();
            const x = ((clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((clientY - rect.top) / rect.height) * 2 + 1;

            if (!this._raycaster) this._raycaster = new window.THREE.Raycaster();
            this._raycaster.setFromCamera({ x, y }, this._camera);

            if (!this._nodeMeshes || this._nodeMeshes.length !== this._nodeEls.length) {
                this._nodeMeshes = this._nodeEls.map(el => el.getObject3D('mesh')).filter(Boolean);
            }

            const intersects = this._raycaster.intersectObjects(this._nodeMeshes, false);
            if (intersects.length > 0) {
                const clickedMesh = intersects[0].object;
                const clickedNodeEl = this._nodeEls.find(el => el.getObject3D('mesh') === clickedMesh);
                if (clickedNodeEl) {
                    const nodeId = Object.keys(this._meshMap).find(id => this._meshMap[id] === clickedNodeEl);
                    if (nodeId) {
                        const node = this._nodes.find(n => n.id === nodeId);
                        if (node && this.onNodeClick) {
                            this._selectedNodeEl = clickedNodeEl;
                            this._updateNodeVisuals();
                            this.onNodeClick(node);
                        }
                    }
                }
            } else {
                if (this._selectedNodeEl !== null) {
                    this._selectedNodeEl = null;
                    this._updateNodeVisuals();
                    if (this.onNodeClick) {
                        this.onNodeClick(null);
                    }
                }
            }
        }

        _handleMouseMove(clientX, clientY) {
            const rect = this.container.getBoundingClientRect();
            const x = ((clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((clientY - rect.top) / rect.height) * 2 + 1;

            if (!this._raycaster) this._raycaster = new window.THREE.Raycaster();
            this._raycaster.setFromCamera({ x, y }, this._camera);

            if (!this._nodeMeshes || this._nodeMeshes.length !== this._nodeEls.length) {
                this._nodeMeshes = this._nodeEls.map(el => el.getObject3D('mesh')).filter(Boolean);
            }

            const intersects = this._raycaster.intersectObjects(this._nodeMeshes, false);
            let hoveredEl = null;
            if (intersects.length > 0) {
                const clickedMesh = intersects[0].object;
                hoveredEl = this._nodeEls.find(el => el.getObject3D('mesh') === clickedMesh) || null;
            }

            if (hoveredEl !== this._hoveredNodeEl) {
                this._hoveredNodeEl = hoveredEl;
                this._updateNodeVisuals();
            }
        }

        _updateNodeVisuals() {
            if (!this._nodeEls || this._nodeEls.length === 0) return;
            const stockMap = this._getStockCubePriceMap();

            let selectedNodeId = null;
            if (this._selectedNodeEl) {
                selectedNodeId = Object.keys(this._meshMap).find(id => this._meshMap[id] === this._selectedNodeEl) || null;
            }

            this._nodeEls.forEach(el => {
                const node = this._nodes.find(n => this._meshMap[n.id] === el);
                if (!node) return;

                const isSphere = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
                const isHovered = (el === this._hoveredNodeEl);
                const isSelected = (el === this._selectedNodeEl);

                const materialConfig = window.NewsGraph.getNodeMaterialConfig(
                    node,
                    stockMap,
                    this._preIpoSymbols,
                    isSelected,
                    isHovered,
                    false
                );
                el.setAttribute('material', materialConfig);

                const isFresh = node.freshness && (node.freshness.status === 'inflection' || node.freshness.status === 'new');
                if (isFresh) {
                    el.userData.isFresh = true;
                    const isNeutral = !node.direction || node.direction === 'neutral';
                    const isLight = this._isLight;
                    const baseColorHex = window.NewsGraph.getColor(node.direction);
                    const colorStr = '#' + baseColorHex.toString(16).padStart(6, '0');

                    if (isLight) {
                        el.userData.emissiveMin = 0.0;
                        el.userData.emissiveMax = isNeutral ? 0.05 : 0.2;
                    } else {
                        el.userData.emissiveMin = 0.05;
                        el.userData.emissiveMax = 0.6;
                    }

                    const glowSprite = el.getObject3D('glowSprite');
                    if (glowSprite) {
                        const THREE = window.THREE || (window.AFRAME && window.AFRAME.THREE);
                        if (isLight) {
                            glowSprite.material.blending = THREE.NormalBlending;
                            glowSprite.material.color.set(isNeutral ? '#666677' : colorStr);
                        } else {
                            glowSprite.material.blending = THREE.AdditiveBlending;
                            glowSprite.material.color.set(isNeutral ? '#ccccdd' : colorStr);
                        }
                        glowSprite.material.needsUpdate = true;
                    }
                } else {
                    el.userData.isFresh = false;
                }

                window.NewsGraph.updateSelectionOutline(el, isSphere, el.userData.radius, isSelected);
            });

            if (this._edgesGroup) {
                this._edgesGroup.children.forEach(mesh => {
                    if (!mesh.material) return;
                    const isConnected = selectedNodeId &&
                        (mesh.userData.sourceId === selectedNodeId || mesh.userData.targetId === selectedNodeId);
                    if (isConnected) {
                        mesh.material.color.set(0x38bdf8); // sky-blue
                        mesh.material.opacity = Math.min(1.0, (mesh.userData.baseOpacity || 0.5) + 0.35);
                    } else {
                        mesh.material.color.set(0x94a3b8); // default grey
                        mesh.material.opacity = mesh.userData.baseOpacity || 0.5;
                    }
                    mesh.material.needsUpdate = true;
                });
            }
        }

        _updateLayoutAndCamera() {
            const aspect = this.container.clientWidth / this.container.clientHeight;
            if (!aspect || isNaN(aspect)) return;

            let scaleX = 1.0;
            let scaleY = 1.0;
            if (aspect >= 1) {
                scaleX = aspect;
                scaleY = 1.0;
            } else {
                scaleX = 1.0;
                scaleY = 1 / aspect;
            }

            this._scaleX = scaleX;
            this._scaleY = scaleY;

            this._nodes.forEach(node => {
                const el = this._meshMap[node.id];
                const orig = this._originalPositions.get(node.id);
                if (el && orig) {
                    el.object3D.position.x = orig.x * scaleX;
                    el.object3D.position.y = orig.y * scaleY;
                    el.object3D.position.z = orig.z;
                }
            });

            let maxNarBoundary = 0;
            this._nodes.forEach(node => {
                if (window.NewsGraph.NARRATIVE_TYPES.has(node.type)) {
                    const el = this._meshMap[node.id];
                    if (el) {
                        const dist = el.object3D.position.length();
                        const r = el.userData.radius || 0;
                        if (dist + r > maxNarBoundary) {
                            maxNarBoundary = dist + r;
                        }
                    }
                }
            });
            this._separationRadius = maxNarBoundary > 0 ? (maxNarBoundary + 0.15) : 2.8;

            this._nodes.forEach(node => {
                if (!window.NewsGraph.NARRATIVE_TYPES.has(node.type)) {
                    const el = this._meshMap[node.id];
                    if (el) {
                        const pos = el.object3D.position;
                        const dist = pos.length();
                        const r = el.userData.radius || 0;
                        const minDist = this._separationRadius + r + 0.3;
                        if (dist < minDist && dist > 0.001) {
                            pos.normalize().multiplyScalar(minDist);
                        }
                    }
                }
            });

            if (this._edgesGroup) {
                this._pivot.remove(this._edgesGroup);
                this._edgesGroup.traverse(child => {
                    if (child.isMesh) {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) child.material.dispose();
                    }
                });
            }
            this._edgesGroup = window.NewsGraph.NewsGraphBuilder.buildEdges(this._links, this._meshMap, this._pivot);

            this._rebuildOrbitSphere();

            this._adjustCameraToFit();
        }

        _rebuildOrbitSphere() {
            if (this._orbitSphere) {
                this._pivot.remove(this._orbitSphere);
                this._orbitSphere.traverse(child => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) child.material.dispose();
                });
                this._orbitSphere = null;
            }
            this._orbitSphere = window.NewsGraph.NewsGraphBuilder.buildOrbitSphere(
                this._separationRadius || 2.8, this._pivot, this._isLight
            );
        }

        _adjustCameraToFit() {
            let maxX = 0;
            let maxY = 0;
            this._nodes.forEach(node => {
                const el = this._meshMap[node.id];
                if (!el) return;
                const pos = el.object3D.position;
                const r = el.userData.radius || 0.5;
                maxX = Math.max(maxX, Math.abs(pos.x) + r);
                maxY = Math.max(maxY, Math.abs(pos.y) + r);
            });

            if (maxX === 0) maxX = 5;
            if (maxY === 0) maxY = 5;

            const padding = 1.05;
            const fitX = maxX * padding;
            const fitY = maxY * padding;

            const camera = this._camera;
            const fov = camera.fov * (Math.PI / 180);
            const aspect = this.container.clientWidth / this.container.clientHeight;

            if (!aspect || isNaN(aspect)) return;

            camera.aspect = aspect;
            camera.updateProjectionMatrix();
            
            let zDistV = fitY / Math.tan(fov / 2);
            let zDistH = fitX / (Math.tan(fov / 2) * aspect);

            let dist = Math.max(zDistV, zDistH);

            if (!this._origCameraPos) {
                this._origCameraPos = camera.position.clone();
                this._origCameraRot = camera.rotation.clone();
                this._origCameraFov = camera.fov;
            }

            camera.position.set(0, 0, dist);
            camera.lookAt(0, 0, 0);
            this._baseDistToCamera = dist;

        }

        _initAframeScene() {
            const THREE = window.THREE;
            const sceneEl = document.getElementById('main-scene');
            
            this._sceneEl = sceneEl;
            this._scene = sceneEl.object3D;
            this._renderer = sceneEl.renderer;
            this._camera = sceneEl.camera;
            this._origEnvMap = this._scene.environment;
            
            const centerPanelContent = document.getElementById('large-center-panel-content');
            if (centerPanelContent && centerPanelContent.object3D) {
                centerPanelContent.object3D.visible = false;
                this._hiddenContent = centerPanelContent;
            }

            const pivotEl = document.createElement('a-entity');
            pivotEl.setAttribute('id', 'news-graph-pivot');
            sceneEl.appendChild(pivotEl);
            this._pivotEl = pivotEl;
            this._pivot = pivotEl.object3D;

            const lightFront = document.createElement('a-light');
            lightFront.setAttribute('type', 'point');
            lightFront.setAttribute('color', '#ffffff');
            lightFront.setAttribute('intensity', '0.6');
            lightFront.setAttribute('distance', '0');
            lightFront.setAttribute('decay', '0');
            lightFront.setAttribute('position', '0 0 10');
            pivotEl.appendChild(lightFront);

            const lightTR = document.createElement('a-light');
            lightTR.setAttribute('type', 'point');
            lightTR.setAttribute('color', '#ffffff');
            lightTR.setAttribute('intensity', '0.8');
            lightTR.setAttribute('distance', '0');
            lightTR.setAttribute('decay', '0');
            lightTR.setAttribute('position', '6 6 4');
            pivotEl.appendChild(lightTR);

            const lightBL = document.createElement('a-light');
            lightBL.setAttribute('type', 'point');
            lightBL.setAttribute('color', '#cce0ff');
            lightBL.setAttribute('intensity', '0.5');
            lightBL.setAttribute('distance', '0');
            lightBL.setAttribute('decay', '0');
            lightBL.setAttribute('position', '-6 -4 4');
            pivotEl.appendChild(lightBL);

            const lightTop = document.createElement('a-light');
            lightTop.setAttribute('type', 'point');
            lightTop.setAttribute('color', '#ffffff');
            lightTop.setAttribute('intensity', '0.7');
            lightTop.setAttribute('distance', '0');
            lightTop.setAttribute('decay', '0');
            lightTop.setAttribute('position', '0 10 2');
            pivotEl.appendChild(lightTop);

            const lightRim = document.createElement('a-light');
            lightRim.setAttribute('type', 'point');
            lightRim.setAttribute('color', '#aabbdd');
            lightRim.setAttribute('intensity', '0.4');
            lightRim.setAttribute('distance', '0');
            lightRim.setAttribute('decay', '0');
            lightRim.setAttribute('position', '0 2 -8');
            pivotEl.appendChild(lightRim);

            const labelRoot = document.createElement('div');
            labelRoot.style.cssText = 'position:absolute;inset:0;z-index:6;pointer-events:none;overflow:hidden;';
            this.container.appendChild(labelRoot);
            this._labelRoot = labelRoot;
        }

        _updateLabels() {
            const THREE = window.THREE;
            const W     = this.container.clientWidth;
            const H     = this.container.clientHeight;
            const v     = new THREE.Vector3();

            if (!this._raycaster) this._raycaster = new THREE.Raycaster();
            const raycaster = this._raycaster;

            if (!this._nodeMeshes || this._nodeMeshes.length !== this._nodeEls.length) {
                this._nodeMeshes = this._nodeEls.map(el => el.getObject3D('mesh')).filter(Boolean);
            }

            this._nodes.forEach(node => {
                const el = this._meshMap[node.id];
                const div  = this._labelMap[node.id];
                if (!el || !div) return;

                v.setFromMatrixPosition(el.object3D.matrixWorld);
                const distToCamera = v.distanceTo(this._camera.position);

                const dir = v.clone().sub(this._camera.position).normalize();
                raycaster.set(this._camera.position, dir);
                const intersects = raycaster.intersectObjects(this._nodeMeshes || []);
                let occluded = false;
                if (intersects.length > 0 && intersects[0].object !== el.getObject3D('mesh')) {
                    occluded = true;
                }

                v.project(this._camera);

                if (v.z > 1 || occluded) { div.style.display = 'none'; return; }

                const sx = (v.x *  0.5 + 0.5) * W;
                const sy = (v.y * -0.5 + 0.5) * H;

                div.style.display = 'block';
                div.style.left    = sx + 'px';
                div.style.top     = sy + 'px';
                
                const baseDist = this._baseDistToCamera || 12;
                const scale = Math.max(1.0, Math.min(4.0, baseDist / distToCamera));
                div.style.transform = `translate(-50%, -50%) scale(${scale})`;
                div.style.zIndex = Math.max(1, Math.round(10000 - distToCamera * 500));
            });
        }

        _getStockCubePriceMap() {
            return this._tickerPriceMap || new Map();
        }

        _updateTheme() {
            const isLight = this._isLight;

            const defaultLabelColor = isLight ? '#111827' : 'var(--color-text-primary,#e2e8f0)';
            const textShadowLight = 'none';
            const textShadowDark = '0 1px 3px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,1)';

            if (this._labelMap) {
                this._nodes.forEach(node => {
                    const div = this._labelMap[node.id];
                    if (!div) return;
                    
                    div.style.color = defaultLabelColor;
                    div.style.textShadow = isLight ? textShadowLight : textShadowDark;
                });
            }

            if (this._orbitSphere !== undefined) {
                this._rebuildOrbitSphere();
            }

            this._updateNodeVisuals();
        }

        _orientCones() {
            if (this._coneInfos.length === 0) return;

            const THREE = window.THREE;

            if (!this._coneDirectionsSet) {
                const stockMap = this._getStockCubePriceMap();
                if (stockMap.size > 0) {
                    this._coneInfos.forEach(info => {
                        info.flipped = window.NewsGraph.isConeFlipped(info.node, stockMap, this._preIpoSymbols);
                    });
                    this._coneDirectionsSet = true;
                }
            }

            const pivotQuat = this._pivot.quaternion.clone().invert();

            const flipQuat = new THREE.Quaternion().setFromAxisAngle(
                new THREE.Vector3(1, 0, 0), Math.PI
            );

            this._coneInfos.forEach(info => {
                const q = pivotQuat.clone();
                if (info.flipped) q.multiply(flipQuat);
                info.el.object3D.quaternion.copy(q);
            });
        }

        _sortRenderOrder() {
            const THREE = window.THREE;
            const camPos = this._camera.position;
            const v = new THREE.Vector3();

            this._nodeEls.forEach(el => {
                v.setFromMatrixPosition(el.object3D.matrixWorld);
                const dist = v.distanceTo(camPos);
                const mesh = el.getObject3D('mesh');
                if (mesh) mesh.renderOrder = -Math.round(dist * 100);
            });
        }

        _animate() {
            if (this._destroyed) return;

            this._controls.update();

            this._orientCones();
            this._sortRenderOrder();
            this._updateLabels();
            this._updateGlowPulse();

            this._animId = requestAnimationFrame(() => this._animate());
        }

        _updateGlowPulse() {
            const time = performance.now();
            if (this._nodeEls) {
                window.NewsGraph.updateGlowPulse(this._nodeEls, time, 1.01, 1.08);
            }
        }

        selectNodeById(nodeId, triggerCallback = true) {
            if (!nodeId) {
                this._selectedNodeEl = null;
                this._updateNodeVisuals();
                return;
            }

            const cleanId = String(nodeId).toUpperCase().trim();
            const node = this._nodes.find(n =>
                String(n.id).toUpperCase().trim() === cleanId ||
                String(n.symbol || '').toUpperCase().trim() === cleanId ||
                String(n.label || '').toUpperCase().trim() === cleanId
            );

            if (node) {
                const nodeEl = this._meshMap[node.id];
                if (nodeEl) {
                    this._selectedNodeEl = nodeEl;
                    this._updateNodeVisuals();
                    if (triggerCallback && this.onNodeClick) {
                        this.onNodeClick(node);
                    }
                }
            } else {
                this._selectedNodeEl = null;
                this._updateNodeVisuals();
            }
        }

        destroy() {
            this._destroyed = true;
            if (this._animId) cancelAnimationFrame(this._animId);
            if (this._themeObserver) this._themeObserver.disconnect();
            if (this._resizeObserver) this._resizeObserver.disconnect();

            if (this._orbitSphere) {
                if (this._pivot) this._pivot.remove(this._orbitSphere);
                this._orbitSphere.traverse(child => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) child.material.dispose();
                });
                this._orbitSphere = null;
            }
            
            if (this._onMouseMoveBound) {
                this.container.removeEventListener('mousemove', this._onMouseMoveBound);
            }
            if (this._onMouseLeaveBound) {
                this.container.removeEventListener('mouseleave', this._onMouseLeaveBound);
            }
            
            if (this._controls) this._controls.destroy();

            this._labelRoot?.remove();

            if (this._pivotEl && this._pivotEl.parentNode) {
                this._pivotEl.parentNode.removeChild(this._pivotEl);
            }

            if (this._sceneEl && this._sceneEl.object3D) {
                this._sceneEl.object3D.environment = this._origEnvMap || null;
            }
            if (this._sceneEl) {
                const stockCube = document.querySelector('#stock-3d-cube');
                const comp = stockCube && stockCube.components && stockCube.components['stock-cube-visualizer'];
                if (comp) {
                    this._sceneEl.setAttribute('bloom', {
                        enabled: !!comp.getConfigSetting('isGlowOn', false) && comp.getQualityLevel() === 'quality',
                        isolateLabels: comp.platformConfig.platform === 'desktop'
                    });
                } else {
                    this._sceneEl.removeAttribute('bloom');
                }
            }
            if (this._envMap) {
                this._envMap.dispose();
                this._envMap = null;
            }

            if (this._hiddenContent && this._hiddenContent.object3D) {
                this._hiddenContent.object3D.visible = true;
            }

            if (this._origCameraPos) {
                this._camera.position.copy(this._origCameraPos);
            }
            if (this._origCameraRot) {
                this._camera.rotation.copy(this._origCameraRot);
            }
        }
    }

    window.NewsGraphVisualizer = NewsGraphVisualizer;
})();
